/**
 * Daylight or dark, and who decides.
 *
 * ⚠️ **This is not a preference feature.** §6 says it about the map — the dark theme is "nearly
 * unreadable under midday sun, which is when most searching happens" — and the rest of the
 * interface is read in the same sun. A coordinator working from a vehicle at noon and one working
 * from a room at 3 a.m. need different palettes, and neither should have to squint at the other's.
 */
export type ThemeMode = 'system' | 'light' | 'dark';

/** What actually gets painted, once `system` has been resolved. */
export type ResolvedMode = 'light' | 'dark';

const STORAGE_KEY = 'aoun.theme-mode';

/**
 * ⚠️ `system` is the default, not `dark`.
 *
 * The operating system already knows whether this machine is in a dark room or a bright one — it
 * asked, or it worked it out from the hour. Overriding that by default means every operator has to
 * find this setting before the panel matches everything else on their screen.
 */
export function readMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);

  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

export function writeMode(mode: ThemeMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
}

/** The media query that answers what `system` means right now. */
export function darkMediaQuery(): MediaQueryList | null {
  // ⚠️ Guarded: jsdom has no `matchMedia`, and a theme module that throws on import takes every
  // test with it — the same way `maplibre-gl` took ten suites down at once.
  return typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
}

export function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode !== 'system') {
    return mode;
  }

  // No `matchMedia` means no way to ask, and dark is what the panel has always been.
  return darkMediaQuery()?.matches === false ? 'light' : 'dark';
}
